import { useEffect, useState, useMemo } from 'react';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Search, AlertTriangle, Clock, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate, fmtDateTime } from '../lib/date';
import { useAuth } from '../lib/AuthContext';

const urgencyStyles = {
  HIGH: 'bg-[#8A3A3A]/15 text-[#8A3A3A] border-[#8A3A3A]/30',
  MED:  'bg-[#B08A3A]/20 text-[#B08A3A] border-[#B08A3A]/40',
  LOW:  'bg-[#839E97]/15 text-[#839E97] border-[#839E97]/30',
};

const bucketOrder = ['0-30', '30-60', '60+'];
const bucketLabel = { '0-30': '0–30 days', '30-60': '30–60 days', '60+': '60+ days' };
const bucketTone = {
  '0-30': 'text-[#839E97]',
  '30-60': 'text-[#B08A3A]',
  '60+': 'text-[#8A3A3A]',
};

function Sparkline({ series, label }) {
  if (!series || series.length === 0) return <span className="text-[#685B72]">—</span>;
  const max = Math.max(1, ...series);
  const w = 84, h = 22, pad = 2;
  const step = (w - pad * 2) / Math.max(1, series.length - 1);
  const pts = series.map((v, i) => {
    const x = pad + i * step;
    const y = h - pad - (v / max) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const areaPts = `${pad},${h - pad} ${pts} ${w - pad},${h - pad}`;
  const last = series[series.length - 1] || 0;
  const prev = series[series.length - 2] || 0;
  const trendUp = last > prev;
  const flat = last === prev;
  const color = flat ? '#685B72' : trendUp ? '#8A3A3A' : '#839E97';
  return (
    <span className="inline-flex items-center gap-2" title={label} data-testid="pn-sparkline">
      <svg width={w} height={h} className="align-middle">
        <polygon points={areaPts} fill={color} fillOpacity="0.15" />
        <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" />
        <circle cx={pad + (series.length - 1) * step} cy={h - pad - (last / max) * (h - pad * 2)} r="2" fill={color} />
      </svg>
    </span>
  );
}

export default function PendingNotes() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const { user } = useAuth();
  const canEditSettings = ['super_user', 'admin'].includes(user?.role);

  const load = () => { api.get('/reports/pending-notes').then(r => setData(r.data)); };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!q) return data.groups;
    return data.groups
      .map(g => ({ ...g, items: g.items.filter(i => `${i.client} ${g.clinician}`.toLowerCase().includes(q.toLowerCase())) }))
      .filter(g => g.items.length > 0);
  }, [data, q]);

  if (!data) return <div className="text-[#685B72]" data-testid="pn-loading">Loading pending notes…</div>;

  const bucketTotals = data.buckets || { '0-30': 0, '30-60': 0, '60+': 0 };

  return (
    <div className="space-y-6" data-testid="pending-notes-page">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#685B72]/70 font-semibold">Compliance</div>
          <h1 className="font-serif text-4xl text-[#2D2833] mt-1">Pending Notes</h1>
          <p className="text-[#685B72] mt-1">{data.total} appointments with <span className="font-semibold">NO NOTE</span>, across {data.groups.length} clinicians.</p>
          {typeof data.min_business_days === 'number' && (
            <p className="text-[11px] text-[#685B72]/70 mt-1" data-testid="pn-grace-note">
              Notes are only flagged once they're more than <span className="font-semibold">{data.min_business_days} business day{data.min_business_days === 1 ? '' : 's'}</span> past the visit date.
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {canEditSettings && (
            <Button
              variant="outline"
              className="h-8 rounded-full text-[12px] font-semibold"
              onClick={() => setShowSettings(true)}
              data-testid="pn-settings-btn"
            >
              <Settings size={13} className="mr-1.5" /> Settings
            </Button>
          )}
          <div className="text-xs text-[#685B72]" data-testid="pn-last-import">
            SP data last imported: <span className="font-semibold text-[#2D2833]">{fmtDateTime(data.last_import_at || data.generated_at)}</span>
          </div>
        </div>
      </div>

      {showSettings && (
        <PendingNotesSettings
          onClose={() => setShowSettings(false)}
          onSaved={() => { setShowSettings(false); load(); }}
        />
      )}

      {/* KPI row — total + aging bucket totals */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="pn-kpis">
        <div className="rounded-2xl border border-[#E8E5EC] p-5 bg-[#8A3A3A]/10">
          <div className="text-xs text-[#685B72] uppercase tracking-widest font-semibold flex items-center gap-1"><AlertTriangle size={12} /> Total NO NOTE</div>
          <div className="font-serif text-3xl mt-1 text-[#2D2833]" data-testid="pn-total">{data.total}</div>
        </div>
        {bucketOrder.map(b => (
          <div key={b} className="rounded-2xl border border-[#E8E5EC] p-5 bg-white">
            <div className="text-xs text-[#685B72] uppercase tracking-widest font-semibold flex items-center gap-1"><Clock size={12} /> {bucketLabel[b]}</div>
            <div className={`font-serif text-3xl mt-1 ${bucketTone[b]}`} data-testid={`pn-bucket-total-${b}`}>{bucketTotals[b] || 0}</div>
          </div>
        ))}
      </div>

      {/* Summary table — NO NOTE counts grouped by clinician with aging buckets */}
      <Card className="rounded-2xl border-[#E8E5EC] p-6" data-testid="pn-clinician-summary">
        <div className="mb-4">
          <div className="text-xs uppercase tracking-widest text-[#685B72] font-semibold">Summary by clinician</div>
          <h3 className="font-serif text-2xl text-[#2D2833] mt-1">NO NOTE counts &amp; aging</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pn-summary-table">
            <thead>
              <tr className="bg-[#FAF8FB] text-[#685B72] text-left">
                <th className="px-3 py-2 font-semibold">Clinician</th>
                <th className="px-3 py-2 font-semibold text-center">Total NO NOTE</th>
                <th className="px-3 py-2 font-semibold text-center">0–30 days</th>
                <th className="px-3 py-2 font-semibold text-center">30–60 days</th>
                <th className="px-3 py-2 font-semibold text-center">60+ days</th>
                <th className="px-3 py-2 font-semibold text-center">Oldest (days)</th>
                <th className="px-3 py-2 font-semibold text-center">8-wk trend</th>
              </tr>
            </thead>
            <tbody>
              {data.groups.map(g => {
                const b = g.buckets || { '0-30': 0, '30-60': 0, '60+': 0 };
                const rowKey = g.clinician.replace(/\W+/g, '_');
                return (
                  <tr key={g.clinician} className="border-t border-[#E8E5EC]" data-testid={`pn-summary-row-${rowKey}`}>
                    <td className="px-3 py-2 text-[#2D2833] font-medium">{g.clinician}</td>
                    <td className="px-3 py-2 text-center font-mono text-[#2D2833] font-semibold">{g.count}</td>
                    <td className="px-3 py-2 text-center font-mono text-[#839E97]">{b['0-30'] || 0}</td>
                    <td className="px-3 py-2 text-center font-mono text-[#B08A3A]">{b['30-60'] || 0}</td>
                    <td className="px-3 py-2 text-center font-mono text-[#8A3A3A] font-semibold">{b['60+'] || 0}</td>
                    <td className="px-3 py-2 text-center font-mono text-[#685B72]">{g.oldest_calendar_days ?? g.oldest_days}</td>
                    <td className="px-3 py-2 text-center">
                      <Sparkline series={g.weekly_series} label={`NO NOTE by ISO week for ${g.clinician}`} />
                    </td>
                  </tr>
                );
              })}
              {data.groups.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-[#685B72]">All caught up — no NO NOTE appointments.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Search + detailed breakdown per clinician */}
      <Card className="rounded-2xl border-[#E8E5EC] p-4" data-testid="pn-search-bar">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#685B72]" />
          <Input placeholder="Search client or clinician…" value={q} onChange={e => setQ(e.target.value)} className="pl-9 rounded-xl" data-testid="pn-search" />
        </div>
      </Card>

      {filtered.length === 0 ? (
        <Card className="rounded-2xl border-[#E8E5EC] p-8 text-center text-[#685B72]" data-testid="pn-empty">
          {data.total === 0
            ? 'All caught up — no pending notes! Great work.'
            : 'No matches for that search.'}
        </Card>
      ) : filtered.map(g => (
        <Card key={g.clinician} className="rounded-2xl border-[#E8E5EC] p-6" data-testid={`pn-group-${g.clinician.replace(/\W+/g,'_')}`}>
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <h3 className="font-serif text-lg text-[#2D2833]">{g.clinician}</h3>
              <div className="text-xs text-[#685B72]">{g.count} pending · oldest {g.oldest_calendar_days ?? g.oldest_days} days</div>
            </div>
            <span className={`stage-badge border ${urgencyStyles[g.oldest_days >= 10 ? 'HIGH' : g.oldest_days >= 5 ? 'MED' : 'LOW']}`}>
              {g.oldest_days >= 10 ? 'HIGH' : g.oldest_days >= 5 ? 'MED' : 'LOW'} urgency
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#FAF8FB] text-[#685B72] text-left">
                  <th className="px-3 py-2 font-semibold">Date of Service</th>
                  <th className="px-3 py-2 font-semibold">Client</th>
                  <th className="px-3 py-2 font-semibold">Code</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold text-right">Days aging</th>
                  <th className="px-3 py-2 font-semibold">Urgency</th>
                </tr>
              </thead>
              <tbody>
                {g.items.map((i, idx) => (
                  <tr key={`${i.date_of_service}-${i.client}-${idx}`} className="border-t border-[#E8E5EC]">
                    <td className="px-3 py-2 text-[#685B72]">{fmtDate(i.date_of_service)}</td>
                    <td className="px-3 py-2 text-[#2D2833] font-medium">{i.client}</td>
                    <td className="px-3 py-2 text-[#685B72]">{i.billing_code}</td>
                    <td className="px-3 py-2 text-[#685B72]">{i.status}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#2D2833]">{i.days_aging ?? i.days_overdue}</td>
                    <td className="px-3 py-2"><span className={`stage-badge border ${urgencyStyles[i.urgency]}`}>{i.urgency}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}
    </div>
  );
}

function PendingNotesSettings({ onClose, onSaved }) {
  const [minBd, setMinBd] = useState('2');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/reports/pending-notes/config')
      .then(r => { setMinBd(String(r.data?.min_business_days ?? 2)); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    const v = parseInt(minBd, 10);
    if (Number.isNaN(v) || v < 0 || v > 30) {
      toast.error('Grace window must be between 0 and 30 business days.');
      return;
    }
    setSaving(true);
    try {
      await api.put('/reports/pending-notes/config', { min_business_days: v });
      toast.success('Settings saved. The list will update immediately.');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="pn-settings-drawer">
        <DialogHeader>
          <DialogTitle>Pending Notes settings</DialogTitle>
          <DialogDescription>
            Set how long clinicians have after a visit before a NO-NOTE appointment starts counting as pending.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-[#685B72] text-sm py-4">Loading current settings…</div>
        ) : (
          <div className="space-y-3 py-2">
            <label className="block text-sm font-semibold text-[#2D2833]">Grace window (business days)</label>
            <Input
              type="number" min="0" max="30" step="1"
              value={minBd}
              onChange={e => setMinBd(e.target.value)}
              data-testid="pn-settings-min-bd"
            />
            <div className="text-[11px] text-[#685B72]">
              Notes are only flagged once they're <span className="font-semibold">more than</span> this many business days past the DOS. Anything within the window is silently excluded from the pending list, counts, and dashboard tile. Default 2. Change takes effect immediately.
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="pn-settings-cancel">Cancel</Button>
          <Button
            onClick={save}
            disabled={saving || loading}
            className="bg-[#685B72] hover:bg-[#4A3B5F] text-white"
            data-testid="pn-settings-save"
          >
            {saving ? 'Saving…' : 'Save settings'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

